import React, { useState, useEffect } from 'react';
import { 
  UsersIcon, 
  EyeIcon, 
  SparklesIcon,
  HomeIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MapPinIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import BookingModal from '../components/BookingModal';
import { Link } from 'react-router-dom';
import useSEO from '../hooks/useSEO';

const features = [
  'Traditional Nepali architecture',
  'Shared family meals',
  'Clean and comfortable bedding',
  'Shared bathroom facilities',
  'Mountain and village views',
  'Cultural immersion experience',
  'Organic garden access',
  'Traditional cooking participation',
  'Local family interaction',
  'Peaceful rural environment'
];

const amenities = [
  'Three traditional meals daily',
  'Clean bed linens and towels',
  'Access to all common areas',
  'Participation in family activities',
  'Cultural orientation and guidance',
  'Traditional welcome ceremony',
  'Access to organic garden',
  'Basic WiFi in common areas'
];

export default function Rooms() {
  const [homestayImages, setHomestayImages] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const openBookingModal = () => setIsBookingModalOpen(true);
  const closeBookingModal = () => setIsBookingModalOpen(false);

  // Fetch room images from public/data/rooms/rooms.json
  useEffect(() => {
    const fetchRoomsImages = async () => {
      try {
        const response = await fetch('/data/rooms/rooms.json');
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        const data = await response.json();
        if (data.images && Array.isArray(data.images)) {
          setHomestayImages(data.images);
          setError(null);
        } else {
          throw new Error('Invalid data format: expected images array');
        }
      } catch (err) {
        setError('Failed to load room images.');
        setHomestayImages([]);
      } finally {
        setLoading(false);
      }
    };
    fetchRoomsImages();
  }, []);

  // Check for mobile screen size
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Touch handlers for mobile swipe on main image
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && selectedImageIndex < homestayImages.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1);
    } else if (isRightSwipe && selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
    }
  };

  const selectImage = (index: number) => {
    setSelectedImageIndex(index);
  };

  const nextImage = () => {
    setSelectedImageIndex((prev) => 
      prev < homestayImages.length - 1 ? prev + 1 : 0
    );
  };

  const prevImage = () => {
    setSelectedImageIndex((prev) => 
      prev > 0 ? prev - 1 : homestayImages.length - 1
    );  };

  const roomsStructuredData = {
    "@context": "https://schema.org",
    "@type": "Accommodation",
    "name": "Bethanchok Homestay Rooms",
    "description": "Traditional Nepali homestay rooms offering authentic family living experience with shared meals and cultural immersion",
    "accommodationType": "Homestay",
    "amenityFeature": [
      {"@type": "LocationFeatureSpecification", "name": "Traditional Nepali architecture"},
      {"@type": "LocationFeatureSpecification", "name": "Shared family meals"},
      {"@type": "LocationFeatureSpecification", "name": "Clean and comfortable bedding"},
      {"@type": "LocationFeatureSpecification", "name": "Shared bathroom facilities"},
      {"@type": "LocationFeatureSpecification", "name": "Mountain views"},
      {"@type": "LocationFeatureSpecification", "name": "Cultural immersion experience"}
    ],
    "occupancy": {
      "@type": "QuantitativeValue",
      "maxValue": 2
    }  };

  // SEO Hook
  useSEO({
    title: "Rooms & Accommodation | Bethanchok Homestay | Traditional Nepal Family Stay",
    description: "Discover our traditional Nepali homestay rooms in Bethanchok village. Experience authentic family living with shared meals, cultural immersion, and mountain views. Book your traditional Nepal accommodation today!",
    keywords: "Nepal homestay rooms, traditional Nepal accommodation, Bethanchok rooms, family stay Nepal, authentic Nepal lodging, Nepal village accommodation, traditional Nepali house, homestay bedroom Nepal, cultural immersion accommodation, Nepal mountain view rooms",
    image: "https://bethanchokstay.com/images/rooms/traditional-room.jpg",
    url: "https://bethanchokstay.com/rooms",
    structuredData: roomsStructuredData
  });

  return (
    <div className="bg-white py-24 sm:py-32 page-transition">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-2xl lg:text-center mb-16">
          <h2 className="text-base font-semibold leading-7 text-primary-600">Homestay Accommodation</h2>
          <p className="mt-2 text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
            Live Like a Local Family
          </p>
          <p className="mt-6 text-lg leading-8 text-neutral-600">
            Experience authentic Nepali hospitality in our traditional family home. Share meals, 
            stories, and daily life with our warm and welcoming host family in the heart of Bethanchok village.
          </p>
        </div>        {/* Main Homestay Section */}
        <div className="bg-white rounded-3xl shadow-soft overflow-hidden">          {/* Image Gallery with Main Display and Thumbnails */}
          <div className="relative">
            {/* Main Image Display */}
            <div 
              className="relative aspect-[16/8] max-w-2xl lg:max-w-4xl mx-auto bg-neutral-50 overflow-hidden rounded-t-lg"
              onTouchStart={isMobile ? handleTouchStart : undefined}
              onTouchMove={isMobile ? handleTouchMove : undefined}
              onTouchEnd={isMobile ? handleTouchEnd : undefined}
            >
              <img
                src={homestayImages[selectedImageIndex]}
                alt={`Bethanchok Homestay Room ${selectedImageIndex + 1}`}
                className="h-full w-full object-cover transition-all duration-500"
              />

              {/* Navigation arrows for main image */}
              <button
                onClick={prevImage}
                className={`absolute top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm hover:bg-white rounded-full transition-all duration-200 shadow-lg z-10 ${
                  isMobile ? 'left-2 p-3' : 'left-4 p-3'
                }`}
              >
                <ChevronLeftIcon className={`text-neutral-800 ${isMobile ? 'h-6 w-6' : 'h-5 w-5'}`} />
              </button>
              <button
                onClick={nextImage}
                className={`absolute top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm hover:bg-white rounded-full transition-all duration-200 shadow-lg z-10 ${
                  isMobile ? 'right-2 p-3' : 'right-4 p-3'
                }`}
              >
                <ChevronRightIcon className={`text-neutral-800 ${isMobile ? 'h-6 w-6' : 'h-5 w-5'}`} />
              </button>

              {/* Price Badge */}
              <div className="absolute top-6 right-6 bg-primary-600 text-white px-4 py-2 rounded-full shadow-lg">
                <span className="text-sm font-medium">$10</span>
                <span className="text-xs block">per night</span>
              </div>

              {/* Image counter */}
              <div className="absolute top-6 left-6 bg-neutral-800/80 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm">
                {selectedImageIndex + 1} of {homestayImages.length}
              </div>

              {/* Image overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent pointer-events-none" />
            </div>

            {/* Thumbnail Carousel */}
            <div className="bg-neutral-100 p-3 rounded-b-lg">
              <div className={`flex gap-2 ${isMobile ? 'overflow-x-auto' : 'justify-center'}`}>
                {homestayImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => selectImage(index)}
                    className={`relative flex-shrink-0 aspect-[4/3] w-20 sm:w-24 overflow-hidden rounded-lg transition-all duration-200 ${
                      selectedImageIndex === index
                        ? 'ring-2 ring-primary-500 ring-offset-2 shadow-lg'
                        : 'hover:ring-2 hover:ring-primary-300 hover:ring-offset-1 shadow-md hover:shadow-lg'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`Room thumbnail ${index + 1}`}
                      className="h-full w-full object-cover"
                    />
                    {/* Selected overlay */}
                    {selectedImageIndex === index && (
                      <div className="absolute inset-0 bg-primary-500/10" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-8 lg:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Left Column - Description */}
              <div>
                <div className="flex items-center mb-6">
                  <HomeIcon className="h-8 w-8 text-primary-600 mr-3" />
                  <h3 className="text-2xl font-bold text-neutral-900">Bethanchok Family Homestay</h3>
                </div>
                
                <p className="text-neutral-700 text-lg leading-relaxed mb-6">
                  Welcome to our traditional Nepali home, where you become part of our family. 
                  Experience authentic village life in comfortable, clean accommodations with 
                  stunning mountain and valley views. Our homestay offers a genuine cultural 
                  exchange opportunity with warm hospitality that Nepal is famous for.
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-center text-neutral-700">
                    <UsersIcon className="h-5 w-5 text-primary-600 mr-3" />
                    <span>Accommodates 1-4 guests</span>
                  </div>
                  <div className="flex items-center text-neutral-700">
                    <EyeIcon className="h-5 w-5 text-primary-600 mr-3" />
                    <span>Mountain & village views</span>
                  </div>
                  <div className="flex items-center text-neutral-700">
                    <MapPinIcon className="h-5 w-5 text-primary-600 mr-3" />
                    <span>Bethanchok Village, Kavrepalanchok</span>
                  </div>
                </div>

                {/* Features */}
                <div>
                  <h4 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
                    <SparklesIcon className="h-5 w-5 text-primary-600 mr-2" />
                    Homestay Features
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {features.map((feature, index) => (
                      <div key={index} className="flex items-center">
                        <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-sm text-neutral-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column - What's Included & Booking */}
              <div>
                <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-2xl p-6 mb-8">
                  <h4 className="text-lg font-semibold text-neutral-900 mb-4">
                    What's Included in Your Stay
                  </h4>
                  <div className="space-y-3">
                    {amenities.map((amenity, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-neutral-700">{amenity}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pricing */}
                <div className="bg-white border-2 border-primary-200 rounded-2xl p-6 mb-8">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-primary-600 mb-2">$10</div>
                    <div className="text-sm text-neutral-600 mb-4">per person per night</div>
                    <div className="text-xs text-neutral-500 italic">
                      Includes all meals and activities
                    </div>
                  </div>
                </div>

                {/* Booking Button */}
                <button
                  onClick={openBookingModal}
                  className="w-full inline-flex justify-center items-center px-6 py-4 border border-transparent text-lg font-semibold rounded-xl text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <HomeIcon className="h-6 w-6 mr-3" />
                  Book Your Homestay Experience
                </button>

                <p className="text-center text-sm text-neutral-500 mt-4">
                  Book directly with our family • No hidden fees
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-16 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-3xl p-8 lg:p-12">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-neutral-900 mb-4">
              Why Choose Our Homestay?
            </h3>
            <p className="text-lg text-neutral-700 max-w-3xl mx-auto">
              Unlike hotels, our homestay offers you the chance to live with a local family, 
              sharing meals, stories, and daily experiences. You'll gain insights into Nepali 
              culture that no guidebook can provide.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-soft">
                <HeartIcon className="h-8 w-8 text-primary-600" />
              </div>
              <h4 className="font-semibold text-neutral-900 mb-2">Authentic Experience</h4>
              <p className="text-sm text-neutral-600">
                Live with a real Nepali family and experience genuine local hospitality
              </p>
            </div>
            <div>
              <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-soft">
                <SparklesIcon className="h-8 w-8 text-primary-600" />
              </div>
              <h4 className="font-semibold text-neutral-900 mb-2">Cultural Immersion</h4>
              <p className="text-sm text-neutral-600">
                Participate in daily activities, cooking, and traditional celebrations
              </p>
            </div>
            <div>
              <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-soft">
                <HomeIcon className="h-8 w-8 text-primary-600" />
              </div>
              <h4 className="font-semibold text-neutral-900 mb-2">Home Away From Home</h4>
              <p className="text-sm text-neutral-600">
                Feel the warmth of Nepali hospitality in a comfortable, welcoming environment
              </p>
            </div>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="mt-16 text-center">
          <p className="text-lg text-neutral-600 mb-6">
            Have questions about your homestay experience?
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-6 py-3 border border-primary-300 text-base font-medium rounded-lg text-primary-700 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
          >
            Contact Our Family
          </Link>
        </div>
      </div>

      {/* Booking Modal */}
      <BookingModal isOpen={isBookingModalOpen} closeModal={closeBookingModal} />
    </div>
  );
}